// Opening the New request sheet, from anywhere, with a screenshot of the page
// already in it. (b-01933)
//
// This is a store rather than a prop or a hook because of one ordering
// constraint: **the capture has to run before the sheet paints.** A sheet that
// opens first photographs its own scrim, so the capture cannot live inside
// `CreateRequestModal` — by the time its effects run, the overlay is on screen.
//
// That used to mean whoever opened the sheet had to own the capture, which is why
// f-01928 grew a topbar button of its own. Wrong shape: "new request" already had
// buttons. Now the capture is an ACTION any of them can call, one host renders the
// sheet, and the button that was only there to own the logic is gone.
//
// It also collapses three always-mounted `CreateRequestModal` instances (the
// Requests page, the notification tray, the capture launcher) into one — and with
// them the per-surface draft scoping that existed only because a submit in one
// could resurrect a stale draft in another.

import { create } from 'zustand';
import { capturePage } from './pageCapture';

interface RequestCaptureState {
  /** Whether the global New request sheet is open. */
  open: boolean;
  /** The staged page screenshot, or null when the capture was skipped or failed. */
  capture: File | null;
  /** A capture is in flight — drives the viewfinder, and guards re-entry so
   *  holding the shortcut down cannot start five of them. */
  capturing: boolean;
  /**
   * Capture the page, then open the sheet with the shot staged.
   *
   * Never rejects: a screenshot is an enhancement to filing a request, so a
   * capture that fails opens the sheet without one rather than blocking it.
   */
  startCapture: () => Promise<void>;
  /** Open the sheet with no screenshot at all. */
  openPlain: () => void;
  close: () => void;
}

/** Two frames, not one: the first lets whatever the click closed (a tray, a menu)
 *  be removed from the DOM, the second lets the browser paint without it. One
 *  frame reliably caught the tray still on screen. */
const twoFrames = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

export const useRequestCaptureStore = create<RequestCaptureState>()((set, get) => ({
  open: false,
  capture: null,
  capturing: false,

  startCapture: async () => {
    if (get().capturing) return;
    set({ capturing: true });
    try {
      await twoFrames();
      set({ capture: await capturePage(), open: true });
    } finally {
      set({ capturing: false });
    }
  },

  openPlain: () => set({ capture: null, open: true }),

  close: () => set({ open: false, capture: null }),
}));
